/**
 * RTS-style Camera Controls for Three.js
 * Provides intuitive camera navigation similar to strategy games:
 * - Middle mouse / Shift+Left: Pan
 * - Right mouse: Rotate
 * - Scroll wheel: Zoom
 * - Touch: Two-finger pan, pinch to zoom
 */

import * as THREE from 'three'

export class RTSCameraControls {
  camera: THREE.PerspectiveCamera
  domElement: HTMLElement

  // Camera state using spherical coordinates
  private target = new THREE.Vector3(0, 0, 0)
  private spherical = new THREE.Spherical(15, Math.PI / 3, 0) // radius, phi (vertical), theta (horizontal)

  // Input state
  private isPanning = false
  private isRotating = false
  private lastMouse = { x: 0, y: 0 }

  // Touch state
  private touchStartDistance = 0
  private touchStartRadius = 0
  private lastTouchCenter = { x: 0, y: 0 }
  private isTouchPanning = false
  private isTouchZooming = false

  // Settings
  panSpeed = 0.01
  rotateSpeed = 0.005
  zoomSpeed = 0.1
  minDistance = 3
  maxDistance = 50
  minPhi = 0.1
  maxPhi = Math.PI - 0.1

  // Bound event handlers for cleanup
  private boundOnMouseDown: (e: MouseEvent) => void
  private boundOnMouseMove: (e: MouseEvent) => void
  private boundOnMouseUp: (e: MouseEvent) => void
  private boundOnWheel: (e: WheelEvent) => void
  private boundOnContextMenu: (e: Event) => void
  private boundOnTouchStart: (e: TouchEvent) => void
  private boundOnTouchMove: (e: TouchEvent) => void
  private boundOnTouchEnd: (e: TouchEvent) => void

  constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement) {
    this.camera = camera
    this.domElement = domElement

    // Bind event handlers
    this.boundOnMouseDown = this.onMouseDown.bind(this)
    this.boundOnMouseMove = this.onMouseMove.bind(this)
    this.boundOnMouseUp = this.onMouseUp.bind(this)
    this.boundOnWheel = this.onWheel.bind(this)
    this.boundOnContextMenu = (e: Event) => e.preventDefault()
    this.boundOnTouchStart = this.onTouchStart.bind(this)
    this.boundOnTouchMove = this.onTouchMove.bind(this)
    this.boundOnTouchEnd = this.onTouchEnd.bind(this)

    this.attach()
    this.update()
  }

  private attach(): void {
    this.domElement.addEventListener('mousedown', this.boundOnMouseDown)
    this.domElement.addEventListener('mousemove', this.boundOnMouseMove)
    this.domElement.addEventListener('mouseup', this.boundOnMouseUp)
    this.domElement.addEventListener('mouseleave', this.boundOnMouseUp)
    this.domElement.addEventListener('wheel', this.boundOnWheel, { passive: false })
    this.domElement.addEventListener('contextmenu', this.boundOnContextMenu)

    // Touch support
    this.domElement.addEventListener('touchstart', this.boundOnTouchStart, { passive: false })
    this.domElement.addEventListener('touchmove', this.boundOnTouchMove, { passive: false })
    this.domElement.addEventListener('touchend', this.boundOnTouchEnd)
  }

  private onMouseDown(e: MouseEvent): void {
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
      // Middle mouse or Shift+Left = Pan
      this.isPanning = true
      e.preventDefault()
    } else if (e.button === 2) {
      // Right mouse = Rotate
      this.isRotating = true
      e.preventDefault()
    }
    this.lastMouse = { x: e.clientX, y: e.clientY }
  }

  private onMouseMove(e: MouseEvent): void {
    const dx = e.clientX - this.lastMouse.x
    const dy = e.clientY - this.lastMouse.y
    this.lastMouse = { x: e.clientX, y: e.clientY }

    if (this.isPanning) {
      this.pan(dx, dy)
    }

    if (this.isRotating) {
      this.rotate(dx, dy)
    }
  }

  private onMouseUp(): void {
    this.isPanning = false
    this.isRotating = false
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault()
    const zoomDelta = e.deltaY > 0 ? 1.1 : 0.9
    this.spherical.radius = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, this.spherical.radius * zoomDelta)
    )
  }

  private onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      // Single touch - pan
      this.isTouchPanning = true
      this.lastTouchCenter = {
        x: e.touches[0]!.clientX,
        y: e.touches[0]!.clientY,
      }
    } else if (e.touches.length === 2) {
      // Two fingers - pan and zoom
      e.preventDefault()
      this.isTouchPanning = true
      this.isTouchZooming = true

      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY
      this.touchStartDistance = Math.sqrt(dx * dx + dy * dy)
      this.touchStartRadius = this.spherical.radius

      this.lastTouchCenter = {
        x: (e.touches[0]!.clientX + e.touches[1]!.clientX) / 2,
        y: (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2,
      }
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (e.touches.length === 1 && this.isTouchPanning) {
      const dx = e.touches[0]!.clientX - this.lastTouchCenter.x
      const dy = e.touches[0]!.clientY - this.lastTouchCenter.y
      this.lastTouchCenter = {
        x: e.touches[0]!.clientX,
        y: e.touches[0]!.clientY,
      }
      this.pan(dx, dy)
    } else if (e.touches.length === 2) {
      e.preventDefault()

      // Calculate new touch distance for zoom
      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (this.isTouchZooming && this.touchStartDistance > 0) {
        const scale = this.touchStartDistance / distance
        this.spherical.radius = Math.max(
          this.minDistance,
          Math.min(this.maxDistance, this.touchStartRadius * scale)
        )
      }

      // Pan with two-finger center
      const center = {
        x: (e.touches[0]!.clientX + e.touches[1]!.clientX) / 2,
        y: (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2,
      }
      const panDx = center.x - this.lastTouchCenter.x
      const panDy = center.y - this.lastTouchCenter.y
      this.lastTouchCenter = center
      this.pan(panDx * 0.5, panDy * 0.5) // Reduce sensitivity for two-finger pan
    }
  }

  private onTouchEnd(): void {
    this.isTouchPanning = false
    this.isTouchZooming = false
    this.touchStartDistance = 0
  }

  private pan(dx: number, dy: number): void {
    // Get camera basis vectors
    const right = new THREE.Vector3()
    const up = new THREE.Vector3()
    const forward = new THREE.Vector3()
    this.camera.matrix.extractBasis(right, up, forward)

    // Pan in camera-local XY plane
    const scaleFactor = this.panSpeed * this.spherical.radius
    this.target.addScaledVector(right, -dx * scaleFactor)
    this.target.addScaledVector(up, dy * scaleFactor)
  }

  private rotate(dx: number, dy: number): void {
    this.spherical.theta -= dx * this.rotateSpeed
    this.spherical.phi = Math.max(
      this.minPhi,
      Math.min(this.maxPhi, this.spherical.phi + dy * this.rotateSpeed)
    )
  }

  /**
   * Update camera position based on spherical coordinates
   * Call this in your animation loop
   */
  update(): void {
    // Convert spherical to cartesian and update camera
    const offset = new THREE.Vector3()
    offset.setFromSpherical(this.spherical)

    this.camera.position.copy(this.target).add(offset)
    this.camera.lookAt(this.target)
  }

  /**
   * Set the camera target (look-at point)
   */
  setTarget(x: number, y: number, z: number): void {
    this.target.set(x, y, z)
  }

  /**
   * Set the camera distance from target
   */
  setDistance(distance: number): void {
    this.spherical.radius = Math.max(this.minDistance, Math.min(this.maxDistance, distance))
  }

  /**
   * Clean up event listeners
   */
  dispose(): void {
    this.domElement.removeEventListener('mousedown', this.boundOnMouseDown)
    this.domElement.removeEventListener('mousemove', this.boundOnMouseMove)
    this.domElement.removeEventListener('mouseup', this.boundOnMouseUp)
    this.domElement.removeEventListener('mouseleave', this.boundOnMouseUp)
    this.domElement.removeEventListener('wheel', this.boundOnWheel)
    this.domElement.removeEventListener('contextmenu', this.boundOnContextMenu)
    this.domElement.removeEventListener('touchstart', this.boundOnTouchStart)
    this.domElement.removeEventListener('touchmove', this.boundOnTouchMove)
    this.domElement.removeEventListener('touchend', this.boundOnTouchEnd)
  }
}
